import { TranslatePipe } from '../../shared/i18n/translate.pipe';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  PLATFORM_ID,
  inject
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Video {
  id: string;
  title: string;
  date: string;
}

@Component({
  selector: 'app-contenus-videos',
  standalone: true,
  imports: [TranslatePipe,CommonModule],
  templateUrl: './contenus-videos.html',
  styleUrls: ['./contenus-videos.scss']
})
export class ContenusVideosComponent implements OnInit {

  public allVideos: Video[] = [];
  public featuredVideo: Video | null = null;
  public isLoading = true;
  public errorMessage: string | null = null;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  private apiKey = 'AIzaSyAz6-2hCIaHdUcPXtDOrXG8wDaHgq0NDCc';
  private channelHandle = '@agbeasso';

  private cacheKeyVideos = 'agbe_youtube_videos_cache';
  private cacheKeyTime = 'agbe_youtube_time_cache';
  private cacheDuration = 2 * 60 * 60 * 1000;

  public pageSize = 6;
  public currentPage = 1;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    console.log('ContenusVideosComponent initialisé');

    if (isPlatformBrowser(this.platformId)) {
      console.log('MODE NAVIGATEUR');
      this.loadVideosWithCache();
    } else {
      console.log('MODE SSR');
      this.isLoading = false;
    }
  }

  loadVideosWithCache(): void {
    console.log('1 - loadVideosWithCache');

    const cachedData = localStorage.getItem(this.cacheKeyVideos);
    const cachedTime = localStorage.getItem(this.cacheKeyTime);

    console.log('2 - cache', {
      cachedData,
      cachedTime
    });

    const now = Date.now();

    if (
      cachedData &&
      cachedTime &&
      now - parseInt(cachedTime, 10) < this.cacheDuration
    ) {
      console.log('3 - CACHE VALIDE');

      try {
        this.allVideos = JSON.parse(cachedData);

        console.log('4 - vidéos depuis cache', this.allVideos);

        this.initFeaturedVideo();

        console.log('5 - featured', this.featuredVideo);

        this.isLoading = false;
        return;
      } catch (e) {
        console.error('ERREUR CACHE', e);

        localStorage.removeItem(this.cacheKeyVideos);
        localStorage.removeItem(this.cacheKeyTime);
      }
    }

    console.log('6 - appel API YouTube');

    this.fetchYouTubeChannelData();
  }

  fetchYouTubeChannelData(): void {

    const channelUrl =
      `https://www.googleapis.com/youtube/v3/channels` +
      `?part=contentDetails` +
      `&forHandle=${encodeURIComponent(this.channelHandle)}` +
      `&key=${this.apiKey}`;

    console.log('7 - URL API', channelUrl);

    fetch(channelUrl)
      .then(response => {

        console.log('8 - réponse API', response.status);

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        return response.json();
      })
    .then(data => {

      console.log('9 - données chaîne', data);

      if (data.items && data.items.length > 0) {

        const uploadsPlaylistId =
          data.items[0].contentDetails.relatedPlaylists.uploads;

        this.fetchPlaylistVideos(uploadsPlaylistId);

      } else {

        console.error('CHAÎNE INTROUVABLE');

        this.errorMessage = 'Chaîne YouTube introuvable.';
        this.isLoading = false;

        this.cdr.detectChanges();
      }
    })
    .catch(error => {

      console.error('ERREUR API CHANNEL', error);

      this.errorMessage =
        'Impossible de récupérer les vidéos YouTube.';

      this.isLoading = false;

      this.cdr.detectChanges();
    });
  }

  fetchPlaylistVideos(playlistId: string): void {
    const playlistUrl =
      `https://www.googleapis.com/youtube/v3/playlistItems` +
      `?part=snippet` +
      `&playlistId=${playlistId}` +
      `&maxResults=50` +
      `&key=${this.apiKey}`;

    fetch(playlistUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        return response.json();
      })
      .then(data => {
        if (data.items) {
          this.allVideos = data.items
            .filter(
              (item: any) =>
                item.snippet?.resourceId?.videoId
            )
            .map((item: any) => ({
              id: item.snippet.resourceId.videoId,
              title: item.snippet.title || 'Vidéo sans titre',
              date: item.snippet.publishedAt
            }));

          localStorage.setItem(
            this.cacheKeyVideos,
            JSON.stringify(this.allVideos)
          );

          localStorage.setItem(
            this.cacheKeyTime,
            Date.now().toString()
          );

          this.initFeaturedVideo();
        }

        this.isLoading = false;

        // IMPORTANT
        this.cdr.detectChanges();
      })
      .catch(error => {
        console.error('Erreur API YouTube:', error);

        this.errorMessage =
          'Erreur lors du chargement des vidéos.';

        this.isLoading = false;

        // IMPORTANT
        this.cdr.detectChanges();
      });
  }

  initFeaturedVideo(): void {
    if (this.allVideos.length === 0) {
      return;
    }

    const found = this.allVideos.find(video =>
      video.title
        .toLowerCase()
        .includes('saint-esprit au quotidien')
    );

    this.featuredVideo = found ?? this.allVideos[0];
  }

  loadVideo(videoId: string): void {
    const found = this.allVideos.find(
      video => video.id === videoId
    );

    if (found) {
      this.featuredVideo = found;

      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }

  getSafeVideoUrl(id: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${id}`
    );
  }

  get totalPages(): number {
    return Math.ceil(this.allVideos.length / this.pageSize);
  }

  get paginatedItems(): Video[] {
    const start =
      (this.currentPage - 1) * this.pageSize;

    return this.allVideos.slice(
      start,
      start + this.pageSize
    );
  }

  setPage(page: number): void {
    this.currentPage = page;
  }
}